import { Logging } from "globals/helpers/logging.helper";
import {
  DataItem,
  PaginationDataList,
} from "globals/intefaces/pageination.data.list.interface";
import { makeAutoObservable } from "mobx";
import { Booking } from "schemas/booking.schemas/booking.schema";
import {
  TrainingPlan,
  trainingPlanToJson,
} from "schemas/training.plan.schemas/training.plan.schema";
import { User } from "schemas/user.schemas/user.schema";
import { HttpBookingService } from "services/httpClients/http.booking.client";
import { HttpCustomerService } from "services/httpClients/http.customer.client";
import { HttpTrainingPlanService } from "services/httpClients/http.training.plan.client";
import TrainingPlanStore from "./training.plan.store";
import { toast } from "react-toastify";
import { ChartData } from "schemas/chart.schemas/chart.data.schema";
import { HttpStatisticService } from "services/httpClients/http.statistic.client";

class CustomerStore {
  private readonly _trainingPlanStore: TrainingPlanStore =
    new TrainingPlanStore();

  // Properties
  private _customersDataList: PaginationDataList<User> = {
    data: [],
    pageIndex: 0,
    itemsInPage: 100,
    isLoading: false,
  };

  private _customersDataListSearchResult: User[] = [];

  private _currentSelectedCustomers?: User[] = [];

  private _currentCustomer: DataItem<User> = {
    data: undefined,
    isLoading: false,
  };

  private _customerTrainingPlanDataList: PaginationDataList<TrainingPlan> = {
    data: [],
    pageIndex: 0,
    itemsInPage: 100,
    isLoading: false,
  };

  private _customerBookingDataList: PaginationDataList<Booking> = {
    data: [],
    pageIndex: 0,
    itemsInPage: 100,
    isLoading: false,
  };

  private _studioTimeStatistic: DataItem<ChartData> = {
    data: undefined,
    isLoading: false,
  };

  private _workoutStatistic: DataItem<ChartData> = {
    data: undefined,
    isLoading: false,
  };

  private _bodyStatistic: DataItem<ChartData> = {
    data: undefined,
    isLoading: false,
  };

  constructor() {
    makeAutoObservable(this);
  }

  //! Setter
  setCustomers = (users: User[]): void => {
    this._customersDataList.data = users;
  };

  setCustomersSearchResult = (users: User[]): void => {
    this._customersDataListSearchResult = users;
  };

  setCurrentSelectedCustomers = (users: User[] | undefined): void => {
    this._currentSelectedCustomers = users;
  };

  setCurrentCustomer = (user: User): void => {
    this._currentCustomer.data = user;
  };

  setCustomerTrainingPlans = (trainingPlans: TrainingPlan[]): void => {
    this._customerTrainingPlanDataList.data = trainingPlans;
  };

  setCustomerBookings = (bookings: Booking[]): void => {
    this._customerBookingDataList.data = bookings;
  };

  setStudioTimeStatistic = (chartData: ChartData): void => {
    this._studioTimeStatistic.data = chartData;
  };

  setWorkoutStatistic = (chartData: ChartData): void => {
    this._workoutStatistic.data = chartData;
  };

  setBodyStatistic = (chartData: ChartData): void => {
    this._bodyStatistic.data = chartData;
  };

  //! Getters
  get customers(): PaginationDataList<User> | undefined {
    if (this._customersDataList == null) {
      return;
    }
    return JSON.parse(JSON.stringify(this._customersDataList));
  }

  get customersSearchResult(): User[] | undefined {
    if (this._customersDataListSearchResult == null) {
      return;
    }
    return JSON.parse(JSON.stringify(this._customersDataListSearchResult));
  }

  get currentCustomer(): DataItem<User> | undefined {
    return this._currentCustomer;
  }

  get currentCustomerFullName(): string {
    if (
      this._currentCustomer?.data == null ||
      this._currentCustomer?.data?.firstName == null ||
      this._currentCustomer?.data?.lastName == null
    ) {
      return "";
    }

    return `${this._currentCustomer?.data?.firstName} ${this._currentCustomer?.data?.lastName}`;
  }

  get customerTrainingPlans(): PaginationDataList<TrainingPlan> | undefined {
    if (this._customerTrainingPlanDataList == null) {
      return;
    }
    return JSON.parse(JSON.stringify(this._customerTrainingPlanDataList));
  }

  get customerBookings(): PaginationDataList<Booking> | undefined {
    if (this._customerBookingDataList == null) {
      return;
    }
    return JSON.parse(JSON.stringify(this._customerBookingDataList));
  }

  get currentSelectedCustomers(): User[] | undefined {
    if (this._currentSelectedCustomers == null) {
      return;
    }
    return JSON.parse(JSON.stringify(this._currentSelectedCustomers));
  }

  get studioTimeStatistic(): DataItem<ChartData> | undefined {
    return this._studioTimeStatistic;
  }

  get workoutStatistic(): DataItem<ChartData> | undefined {
    return this._workoutStatistic;
  }

  get bodyStatistic(): DataItem<ChartData> | undefined {
    return this._bodyStatistic;
  }

  //! Methods
  fetchAndSetCustomers = async (args: {
    refresh?: boolean;
    loadMore?: boolean;
  }): Promise<void> => {
    try {
      if (
        args.loadMore !== true &&
        this._customersDataList != null &&
        this._customersDataList.data.length !== 0 &&
        args.refresh !== true
      ) {
        return;
      }

      if (this._customersDataList.isLoading) {
        return;
      }

      if (args.loadMore != null) {
        this._customersDataList.pageIndex += 1;
      } else {
        this._customersDataList.pageIndex = 0;
        this._customersDataList.data = [];
      }

      this._customersDataList.isLoading = true;

      const customers = await HttpCustomerService.getInstance().find({});

      if (customers == null) {
        this._customersDataList.isLoading = false;
        return;
      }

      this.setCustomers(customers);

      this._customersDataList.isLoading = false;
    } catch (err) {
      this._customersDataList.isLoading = false;

      Logging.error({
        className: "CustomerStore",
        methodName: "fetchAndSetCustomers",
        message: "Kunden konnten nicht geladen werden",
        exception: err,
        showAlert: true,
      });
    }
  };

  fetchAndSetCustomer = async (args: { customerID: string }): Promise<void> => {
    try {
      this._currentCustomer.isLoading = true;

      const customer = await HttpCustomerService.getInstance().findOne({
        id: args.customerID,
      });

      if (customer == null) {
        this._currentCustomer.isLoading = false;
        return;
      }

      this.setCurrentCustomer(customer);
      this._currentCustomer.isLoading = false;
    } catch (err) {
      this._currentCustomer.isLoading = false;

      Logging.error({
        className: "CustomerStore",
        methodName: "fetchAndSetCustomer",
        message: "Kunde konnte nicht geladen werden",
        exception: err,
        showAlert: true,
      });
    }
  };

  fetchAndSetCustomerTrainingPlans = async (args: {
    customerID: string;
    refresh?: boolean;
    loadMore?: boolean;
  }): Promise<void> => {
    try {
      if (
        args.loadMore !== true &&
        this._customerTrainingPlanDataList != null &&
        this._customerTrainingPlanDataList.data.length !== 0 &&
        args.refresh !== true
      ) {
        return;
      }

      if (this._customerTrainingPlanDataList.isLoading) {
        return;
      }

      if (args.loadMore != null) {
        this._customerTrainingPlanDataList.pageIndex += 1;
      } else {
        this._customerTrainingPlanDataList.pageIndex = 0;
        this._customerTrainingPlanDataList.data = [];
      }

      this._customerTrainingPlanDataList.isLoading = true;

      const trainingPlans = await HttpTrainingPlanService.getInstance().find({
        query: {
          filter: JSON.stringify({
            $or: [{ type: "CUSTOMER" }, { type: "USER" }],
            user: args.customerID,
          }),
        },
      });

      if (trainingPlans == null) {
        this._customerTrainingPlanDataList.isLoading = false;
        return;
      }

      this.setCustomerTrainingPlans(trainingPlans);
      this._customerTrainingPlanDataList.isLoading = false;
    } catch (err) {
      this._customerTrainingPlanDataList.isLoading = false;

      Logging.error({
        className: "CustomerStore",
        methodName: "fetchAndSetCustomerTrainingPlans",
        message: "Training Pläne konnten nicht geladen werden",
        exception: err,
        showAlert: true,
      });
    }
  };

  fetchAndSetCustomerBookings = async (args: {
    customerID: string;
    refresh?: boolean;
    loadMore?: boolean;
  }): Promise<void> => {
    try {
      if (
        args.loadMore !== true &&
        this._customerBookingDataList != null &&
        this._customerBookingDataList.data.length !== 0 &&
        args.refresh !== true
      ) {
        return;
      }

      if (this._customerBookingDataList.isLoading) {
        return;
      }

      if (args.loadMore != null) {
        this._customerBookingDataList.pageIndex += 1;
      } else {
        this._customerBookingDataList.pageIndex = 0;
        this._customerBookingDataList.data = [];
      }

      this._customerBookingDataList.isLoading = true;

      const bookings =
        await HttpCustomerService.getInstance().getBookingsForCustomer(
          args.customerID
        );

      if (bookings == null) {
        this._customerBookingDataList.isLoading = false;
        return;
      }

      this.setCustomerBookings(bookings);

      this._customerBookingDataList.isLoading = false;
    } catch (err) {
      this._customerBookingDataList.isLoading = false;

      Logging.error({
        className: "CustomerStore",
        methodName: "fetchAndSetCustomerBookings",
        message: "Buchungen konnten nicht geladen werden",
        exception: err,
        showAlert: true,
      });
    }
  };

  updateCustomerTrainingPlan = async (args: {
    id: string;
    trainingPlan: TrainingPlan;
    notifyCustomer?: boolean;
  }): Promise<TrainingPlan | undefined> => {
    try {
      this._customerTrainingPlanDataList.isLoading = true;

      // if notifyCustomer is defined add it to the query
      const query = args.notifyCustomer ? { notifyCustomer: true } : {};

      const updatedPlan = await HttpTrainingPlanService.getInstance().updateOne(
        {
          id: args.id,
          data: trainingPlanToJson(args.trainingPlan),
          query,
        }
      );

      if (updatedPlan == null) {
        this._customerTrainingPlanDataList.isLoading = false;
        return;
      }

      // find updated plan in this._trainingPlanDataList.data and replace it
      // and set plan as first item in list
      const plansWithoutUpdatedItem =
        this._customerTrainingPlanDataList.data.filter(
          (item) => item._id !== updatedPlan._id
        );

      this._customerTrainingPlanDataList.data = [
        updatedPlan,
        ...plansWithoutUpdatedItem,
      ];

      this._trainingPlanStore.setCurrentTrainingPlan(updatedPlan);

      this._customerTrainingPlanDataList.isLoading = false;
      toast.success("TrainingPlan wurde erfolgreich aktualisiert");
      return updatedPlan;
    } catch (err) {
      this._customerTrainingPlanDataList.isLoading = false;

      Logging.error({
        className: "TrainingPlanStore",
        methodName: "updateTrainingPlan",
        message: "TrainingPlan konnte nicht aktualisiert werden",
        exception: err,
        showAlert: true,
      });
    }
  };

  createCustomerTrainingPlan = async (args: {
    trainingPlan: TrainingPlan;
    fromTemplate?: string;
    notifyCustomer?: boolean;
  }): Promise<TrainingPlan | undefined> => {
    try {
      this._customerTrainingPlanDataList.isLoading = true;

      // if notifyCustomer or fromTemplate is true add it to the query
      const query = {
        ...(args.notifyCustomer != null && {
          notifyCustomer: args.notifyCustomer,
        }),
        ...(args.fromTemplate != null && {
          fromTemplate: args.fromTemplate,
        }),
      };

      const newPlan = await HttpTrainingPlanService.getInstance().create({
        data: trainingPlanToJson(args.trainingPlan),
        query,
      });

      if (newPlan == null) {
        this._customerTrainingPlanDataList.isLoading = false;
        return;
      }

      if (this._customerTrainingPlanDataList.data != null) {
        this._customerTrainingPlanDataList.data.unshift(newPlan);
      }

      this._trainingPlanStore.setCurrentTrainingPlan(newPlan);

      this._customerTrainingPlanDataList.isLoading = false;
      toast.success("TrainingPlan wurde erfolgreich erstellt");
      return newPlan;
    } catch (err) {
      this._customerTrainingPlanDataList.isLoading = false;

      Logging.error({
        className: "TrainingPlanStore",
        methodName: "createTrainingPlan",
        message: "TrainingPlan konnte nicht erstellt werden",
        exception: err,
        showAlert: true,
      });
    }
  };

  cancelBookingForCustomer = async (args: {
    bookingID: string;
  }): Promise<void> => {
    try {
      this._customerBookingDataList.isLoading = true;

      const canceledBooking =
        await HttpBookingService.getInstance().cancelBookingFromEvent({
          bookingID: args.bookingID,
        });

      if (canceledBooking == null) {
        this._customerBookingDataList.isLoading = false;
        return;
      }

      // filter out the deleted booking and set the new list
      if (this._customerBookingDataList?.data != null) {
        const filteredBookings = this._customerBookingDataList.data.filter(
          (booking: Booking) => booking._id !== args.bookingID
        );

        this.setCustomerBookings(filteredBookings);
      }

      this._customerBookingDataList.isLoading = false;
    } catch (err) {
      this._customerBookingDataList.isLoading = false;

      Logging.error({
        className: "CustomerStore",
        methodName: "cancelBookingForCustomer",
        message: "Buchung konnte nicht storniert werden",
        exception: err,
        showAlert: true,
      });
    }
  };

  searchAndSetCustomers = async (
    searchTerm: string
  ): Promise<User | undefined> => {
    try {
      if (this._customersDataList.isLoading) {
        return;
      }

      if (searchTerm == null || searchTerm.trim().length === 0) {
        this.fetchAndSetCustomers({ refresh: true });
        return;
      }

      this._customersDataList.isLoading = true;

      const exercises = await HttpCustomerService.getInstance().search({
        searchTerm: searchTerm.trim(),
      });

      if (exercises == null) {
        this._customersDataList.isLoading = false;
        return;
      }

      this.setCustomersSearchResult(exercises);
      this._customersDataList.isLoading = false;
    } catch (err) {
      this._customersDataList.isLoading = false;

      Logging.error({
        className: "CustomerStore",
        methodName: "searchAndSetCustomers",
        message: "Suche konnte nicht durchgeführt werden",
        exception: err,
      });
    }
  };

  getStudioTimeStatistics = async (): Promise<ChartData | undefined> => {
    try {
      if (
        this._studioTimeStatistic.isLoading ||
        this._currentCustomer == null ||
        this._currentCustomer.data == null
      ) {
        return;
      }

      this._studioTimeStatistic.isLoading = true;

      const statistics =
        await HttpStatisticService.getInstance().getStatisticsForTimeInStudio({
          userID: this._currentCustomer.data._id,
        });

      if (statistics == null) {
        this._studioTimeStatistic.isLoading = false;
        return;
      }

      this.setStudioTimeStatistic(statistics);
      this._studioTimeStatistic.isLoading = false;

      return statistics;
    } catch (err) {
      this._studioTimeStatistic.isLoading = false;

      Logging.error({
        className: "CustomerStore",
        methodName: "getStudioTimeStatistics",
        message: "Studio Zeit Statistik konnte nicht geladen werden",
        exception: err,
      });
    }
  };

  getStatisticForWorkouts = async (): Promise<ChartData | undefined> => {
    try {
      if (
        this._workoutStatistic.isLoading ||
        this._currentCustomer == null ||
        this._currentCustomer.data == null
      ) {
        return;
      }

      this._workoutStatistic.isLoading = true;

      const statistics =
        await HttpStatisticService.getInstance().getStatisticForWorkouts({
          userID: this._currentCustomer.data._id,
        });

      if (statistics == null) {
        this._workoutStatistic.isLoading = false;
        return;
      }

      this.setWorkoutStatistic(statistics);
      this._workoutStatistic.isLoading = false;

      return statistics;
    } catch (err) {
      this._workoutStatistic.isLoading = false;

      Logging.error({
        className: "CustomerStore",
        methodName: "getStatisticForWorkouts",
        message: "Workout Statistik konnte nicht geladen werden",
        exception: err,
      });
    }
  };

  getStatisticForBodyStats = async (): Promise<ChartData | undefined> => {
    try {
      if (
        this._bodyStatistic.isLoading ||
        this._currentCustomer == null ||
        this._currentCustomer.data == null
      ) {
        return;
      }

      this._bodyStatistic.isLoading = true;

      const statistics =
        await HttpStatisticService.getInstance().getStatisticForBodyStat({
          userID: this._currentCustomer.data._id,
          type: "BODY_WEIGHT",
        });

      if (statistics == null) {
        this._bodyStatistic.isLoading = false;
        return;
      }

      this.setBodyStatistic(statistics);
      this._bodyStatistic.isLoading = false;

      return statistics;
    } catch (err) {
      this._bodyStatistic.isLoading = false;

      Logging.error({
        className: "CustomerStore",
        methodName: "getStatisticForBodyStats",
        message: "Body Statistik konnte nicht geladen werden",
        exception: err,
      });
    }
  };
}

export default CustomerStore;
